'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSeatSchema } from '@/api/seatSchema';
import { stringToHsl } from '@/components/event-cards/utils';
import { useTranslation } from '@/i18n/client';
import { selectSeat } from '@/store/event-payment/event-payment.actions';
import { selectSelectedTickets } from '@/store/event-payment/event-payment.selectotrs';
import { EventDetails } from '@/types/events';
import { WithLang } from '@/types/general';
import { CurrencyCode } from '@/types/general/base';
import { TicketSelectOption } from '@/types/tickets';

interface Props {
    event: EventDetails;
    ticketOptions: TicketSelectOption[];
}

const setActiveSeat = (circle: SVGCircleElement, ticketOptions: TicketSelectOption[]) => {
    circle.setAttribute('style', 'cursor: pointer');
    circle.dataset.price = String(ticketOptions.find((option) => option.tag === circle.id)?.price);
    circle.dataset.currency = ticketOptions.find((option) => option.tag === circle.id)?.currency_code;
    circle.dataset.description = ticketOptions.find((option) => option.tag === circle.id)?.description;
    circle.dataset.status = 'active';
};

export default function SeatSchemaForm(props: WithLang<Props>) {
    const { t: tCommon } = useTranslation(props.lang, 'common');

    const selectedTickets = useSelector(selectSelectedTickets);

    const dispatch = useDispatch();
    const [initialSvg, setInitialSvg] = useState<string | null>(null);

    const getSvgSchema = async () => {
        if (props.event.seat_schema) {
            const seatSchema = await getSeatSchema(props.event.seat_schema);
            const fileUrl = `${process.env.NEXT_PUBLIC_STORAGE_EDGE_URL}/${seatSchema.filename_disk}`;

            const fetchSvg = async () => {
                if (seatSchema) {
                    const response = await fetch(fileUrl).then((res) => res.text());
                    setInitialSvg(response);
                }
            };
            fetchSvg();
        }
    };

    useEffect(() => {
        getSvgSchema();
    }, []);

    const svgContainerRef = useRef<HTMLDivElement>(null);

    const [zoom, setZoom] = useState(1);

    const zoomIn = () => {
        setZoom(zoom >= 4 ? 4 : zoom + 1);
    };

    const zoomOut = () => {
        setZoom(zoom <= 1 ? 1 : zoom - 1);
    };

    const renderSeatSchema = () => {
        if (!initialSvg) return;

        const parser = new DOMParser();
        const parsedSchema = parser.parseFromString(initialSvg, 'image/svg+xml');
        const circles = parsedSchema.querySelectorAll('circle');

        // @ts-ignore
        for (const circle of circles) {
            const ticketOption = props.ticketOptions.find((option) => option.tag === circle.id);

            if (ticketOption) {
                setActiveSeat(circle, props.ticketOptions);
                setPriceColor(circle);
            } else {
                circle.setAttribute('fill', '#e1e2e3');
                circle.style.stroke = '#cfd0d1';
            }

            if (selectedTickets.length > 0) {
                for (const ticket of selectedTickets) {
                    if (ticket.tag === circle.id) {
                        circle.dataset.status = 'selected';
                        circle.setAttribute('fill', '#fecd70');
                    }
                }
            }
        }

        if (svgContainerRef.current) {
            svgContainerRef.current.innerHTML = ''; // Clear previous SVG if any
            svgContainerRef.current.append(parsedSchema.documentElement); // Inject parsed SVG
        }
    };

    useEffect(() => {
        renderSeatSchema();
    }, [initialSvg]);

    const prices = new Map(
        props.ticketOptions.map((ticket) => [
            `${ticket.price}_${ticket.currency_code}`,
            { price: ticket.price, currency: ticket.currency_code },
        ]),
    );

    const pricesBlock = Array.from(prices.entries()).map((entry, i) => ({
        key: entry[0],
        price: entry[1].price,
        currency: entry[1].currency,
        color: stringToHsl(((i + 1) * 25).toString()),
    }));

    const setPriceColor = (el: SVGCircleElement) => {
        const key = `${el.dataset.price}_${el.dataset.currency}`;
        const color = pricesBlock.find((item) => item.key === key)?.color;
        if (color) {
            el.setAttribute('fill', color);
        }
    };

    const handleSeatClick = (e: any) => {
        const target = e.target as SVGCircleElement;

        if (target) {
            const ticket = {
                tag: target.id,
                price: +target.dataset.price!,
                quantity: 1,
                currency_code: target.dataset.currency as CurrencyCode,
                description: target.dataset.description,
            };

            if (target.dataset.status === 'active') {
                target.dataset.status = 'selected';
                target.setAttribute('fill', '#fecd70');
                dispatch(selectSeat({ ticket }));
            } else if (target.dataset.status === 'selected') {
                target.dataset.status = 'active';
                setPriceColor(target);
                dispatch(selectSeat({ ticket }));
            }
        }
    };

    return (
        <div className="relative flex min-w-full flex-nowrap">
            <div className="absolute right-0 top-60 z-20 flex w-8 -translate-y-1/2 flex-col gap-y-4 md:w-12">
                <button
                    disabled={zoom >= 2}
                    className="icon h-8 w-full border border-solid border-black bg-white text-lg md:h-12 md:text-2xl"
                    onClick={zoomIn}
                >
                    +
                </button>
                <button
                    disabled={zoom <= 1}
                    className="icon h-8 w-full border border-solid border-black bg-white text-lg md:h-12 md:text-2xl"
                    onClick={zoomOut}
                >
                    -
                </button>
            </div>
            <div className={`relative min-h-full min-w-full overflow-auto transition-transform duration-200`}>
                {prices && (
                    <div className="flex flex-wrap items-center justify-center gap-x-3">
                        {pricesBlock.map((item) => (
                            <div className="flex items-center gap-x-1" key={item.key}>
                                <div
                                    className="border-regular h-4 w-4 rounded-full"
                                    style={{ backgroundColor: item.color }}
                                />
                                <span>
                                    {tCommon('price.regular', { currency: item.currency, context: item.price })}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                <div
                    style={{
                        transform: `scale(${zoom})`,
                        transformOrigin: 'center 0',
                    }}
                    className="ease mx-auto mt-4 h-fit w-full object-contain transition-transform duration-200 md:w-[65%]"
                    onClick={(e) => handleSeatClick(e)}
                    ref={svgContainerRef}
                />
            </div>
        </div>
    );
}
